import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PostComposer from "@/components/PostComposer";
import ScheduledManager from "@/components/ScheduledManager";
import { tierFor } from "@/lib/tiers";
import { Image as ImageIcon, BarChart2, CalendarClock } from "lucide-react";

const DRAFT_KEY = "feed-board-draft";

function initials(name) {
    return (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

/**
 * Compact "post to your board" bar for the top of the Feed. Collapsed by default;
 * clicking expands the full PostComposer (photos, GIF, poll, emoji, scheduling)
 * plus scheduled-post management. Posts to /me/posts and notifies the author's
 * followers per their alert settings.
 *
 * - Auto-collapses when you click anywhere outside the expanded composer.
 * - Keeps an unfinished draft (persisted by PostComposer) so a stray collapse
 *   doesn't lose your text; the collapsed bar previews it with a "Draft" chip.
 */
export default function FeedComposer({ onPosted }) {
    const { user } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [tick, setTick] = useState(0);
    const [draft, setDraft] = useState(() => localStorage.getItem(DRAFT_KEY) || "");
    const boxRef = useRef(null);

    const collapse = () => {
        setExpanded(false);
        setDraft(localStorage.getItem(DRAFT_KEY) || "");
    };

    // Close when clicking outside the expanded composer (draft is preserved).
    useEffect(() => {
        if (!expanded) return;
        const onDown = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) collapse();
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [expanded]);

    if (!user) return null;

    const ringColor = tierFor(user.thanks_count || 0).color;
    const Avatar = () => (
        user.photo
            ? <img src={user.photo} alt="" style={{ borderColor: ringColor }} className="h-9 w-9 rounded-full object-cover border-2 bg-white shrink-0" />
            : <span style={{ borderColor: ringColor, color: ringColor }} className="h-9 w-9 rounded-full border-2 bg-[#1a1a1a] flex items-center justify-center text-sm font-display shrink-0">{initials(user.username || user.name)}</span>
    );

    const submit = async (payload) => {
        await api.post("/me/posts", payload);
        toast.success(payload.scheduled_for ? "Post scheduled" : "Posted to your board");
        setTick((t) => t + 1);
        setDraft("");
        setExpanded(false);
        if (!payload.scheduled_for && onPosted) onPosted();
    };

    if (!expanded) {
        const hasDraft = !!draft.trim();
        return (
            <div className="mb-6">
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    data-testid="feed-composer-bar"
                    className="w-full flex items-center gap-3 border border-[#2a2a2a] bg-[#141414] rounded-full pl-3 pr-2 py-2 hover:border-[#ff4500]/60 transition-colors text-left"
                >
                    <Avatar />
                    <span className={`flex-1 min-w-0 text-sm truncate ${hasDraft ? "text-[#cfcfcf]" : "text-[#8a8a8a]"}`}>
                        {hasDraft ? draft : "Share an update with your followers…"}
                    </span>
                    {hasDraft && (
                        <span data-testid="feed-composer-draft-chip" className="hidden sm:inline text-[9px] font-bold uppercase tracking-wider text-[#22c55e] border border-[#22c55e]/40 rounded-full px-2 py-0.5 shrink-0">Draft</span>
                    )}
                    {!hasDraft && (
                        <span className="hidden sm:flex items-center gap-3 text-[#ff4500] mr-1">
                            <ImageIcon className="h-4 w-4" /><BarChart2 className="h-4 w-4" /><CalendarClock className="h-4 w-4" />
                        </span>
                    )}
                    <span className="bg-[#ff4500] text-white rounded-full px-4 py-1.5 text-sm font-semibold shrink-0">{hasDraft ? "Resume" : "Post"}</span>
                </button>
            </div>
        );
    }

    return (
        <div ref={boxRef} className="mb-6 border border-[#2a2a2a] bg-[#141414] rounded-xl p-4" data-testid="feed-composer-expanded">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Post to your board — followers get notified</div>
                <button type="button" onClick={collapse} data-testid="feed-composer-cancel" className="text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-white">Close</button>
            </div>
            <PostComposer onSubmit={submit} accent="#ff4500" placeholder="Post an update to your board — tryouts, results, schedule changes…" testidPrefix="feed-composer" draftKey={DRAFT_KEY} />
            <div className="mt-4"><ScheduledManager key={tick} onChange={() => setTick((t) => t + 1)} /></div>
        </div>
    );
}
